import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Input,
  Select,
  Space,
  message,
  Popconfirm,
  Tag,
  Typography,
  Row,
  Col,
  Alert,
  Modal,
  Form,
  Checkbox,
  Tabs,
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  HolderOutlined,
  DatabaseOutlined,
  EditOutlined,
} from '@ant-design/icons';
import SapFieldSelector from './SapFieldSelector';
import { SapField } from '../services/SAPB1Service';
import { useI18n } from '../hooks/useI18n';

const { Text } = Typography;

interface TemplateVisualEditorProps {
  templateContent?: any;
  onTemplateChange: (content: any) => void;
}

interface TemplateProperty {
  id?: number;
  uid: string; // stable id
  name: string;
  type: string;
  description?: string;
  isRequired: boolean;
  itemsType?: string;
  itemsDescription?: string;
  children?: TemplateProperty[];
  // 拖拽相关
  isDragging?: boolean;
  dragOverIndex?: number;
  // 展开状态
  isExpanded?: boolean;
  sortOrder?: number;
}

const generateUid = (): string => `uid_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

const TemplateVisualEditor: React.FC<TemplateVisualEditorProps> = ({
  templateContent,
  onTemplateChange,
}) => {
  const { t } = useI18n();
  const [properties, setProperties] = useState<TemplateProperty[]>([]);
  const [editingProperty, setEditingProperty] = useState<TemplateProperty | null>(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [selectedSapFields, setSelectedSapFields] = useState<SapField[]>([]);
  const [activeTab, setActiveTab] = useState('manual');
  const [childModalVisible, setChildModalVisible] = useState(false);
  const [parentPropertyName, setParentPropertyName] = useState<string>('');
  const [childSelectedSapFields, setChildSelectedSapFields] = useState<SapField[]>([]);
  const [childActiveTab, setChildActiveTab] = useState('manual');
  
  // 拖拽状态管理
  const [dragSource, setDragSource] = useState<string | null>(null);
  const [dragSourceIndex, setDragSourceIndex] = useState<number>(-1);
  const [dragOverIndex, setDragOverIndex] = useState<number>(-1);
  const [isDragging, setIsDragging] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  // 子级拖拽状态
  const [childDragParentName, setChildDragParentName] = useState<string | null>(null);
  const [childDragSourceIndex, setChildDragSourceIndex] = useState<number>(-1);
  const [childDragOverIndex, setChildDragOverIndex] = useState<number>(-1);
  const [childIsDragging, setChildIsDragging] = useState(false);
  // 孙级拖拽状态
  const [grandDragParentName, setGrandDragParentName] = useState<string | null>(null);
  const [grandDragChildName, setGrandDragChildName] = useState<string | null>(null);
  const [grandDragSourceIndex, setGrandDragSourceIndex] = useState<number>(-1);
  const [grandDragOverIndex, setGrandDragOverIndex] = useState<number>(-1);
  const [grandIsDragging, setGrandIsDragging] = useState(false);

  useEffect(() => {
    console.log('useEffect触发:', {
      templateContent: templateContent,
      hasProperties: !!templateContent?.properties,
      isArray: Array.isArray(templateContent?.properties),
      propertiesLength: templateContent?.properties?.length,
      templateContentId: templateContent?.id,
      templateContentName: templateContent?.name,
      isEditing: isEditing,
      currentPropertiesLength: properties.length
    });
    
    // 如果正在编辑，跳过重新加载数据
    if (isEditing) {
      console.log('正在编辑中，跳过数据重新加载');
      return;
    }
    
    // 如果templateContent无效，但有现有数据，不要清空
    if (!templateContent || !templateContent.properties) {
      console.log('templateContent无效，但有现有数据，保持现有数据');
      if (properties.length > 0) {
        console.log('保持现有properties，不设置为空数组');
        return;
      }
    }
    
    console.log('检查templateContent.properties:', {
      hasProperties: !!templateContent?.properties,
      propertiesType: typeof templateContent?.properties,
      isArray: Array.isArray(templateContent?.properties),
      propertiesValue: templateContent?.properties
    });
    
    if (templateContent?.properties && Array.isArray(templateContent.properties)) {
      const sortByOrder = (arr: any[]) => (arr || []).slice().sort((a, b) => (a.sort_order ?? a.sortOrder ?? 0) - (b.sort_order ?? b.sortOrder ?? 0));

      const mapGrand = (grand: any): TemplateProperty => ({
        id: grand.id,
        uid: String(grand.id ?? generateUid()),
        name: grand.property_name,
        type: grand.property_type,
        description: grand.description || '',
        isRequired: grand.is_required || false,
        itemsType: grand.item_type,
        itemsDescription: grand.array_items?.items_description || '',
        children: [],
        isExpanded: false,
        sortOrder: grand.sort_order ?? 0,
      });

      const mapChild = (child: any): TemplateProperty => ({
        id: child.id,
        uid: String(child.id ?? generateUid()),
        name: child.property_name,
        type: child.property_type,
        description: child.description || '',
        isRequired: child.is_required || false,
        itemsType: child.item_type,
        itemsDescription: child.array_items?.items_description || '',
        children: Array.isArray(child.children) ? sortByOrder(child.children).map(mapGrand) : [],
        isExpanded: false,
        sortOrder: child.sort_order ?? 0,
      });

      const convertedProperties: TemplateProperty[] = sortByOrder(templateContent.properties).map((prop: any) => ({
        id: prop.id,
        uid: String(prop.id ?? generateUid()),
        name: prop.property_name,
        type: prop.property_type,
        description: prop.description || '',
        isRequired: prop.is_required || false,
        children: prop.children && Array.isArray(prop.children) ? sortByOrder(prop.children).map(mapChild) : [],
        itemsType: prop.item_type,
        itemsDescription: prop.array_items?.items_description,
        isExpanded: false,
        sortOrder: prop.sort_order ?? 0,
      }));
      console.log('设置properties:', {
        convertedProperties: convertedProperties,
        length: convertedProperties.length,
        currentPropertiesLength: properties.length,
        expandedProperties: convertedProperties.filter(p => p.isExpanded).map(p => ({
          name: p.name,
          type: p.type,
          isExpanded: p.isExpanded,
          hasChildren: p.children?.length || 0
        }))
      });
      
      // 只有当数据真正发生变化时才更新
      if (JSON.stringify(convertedProperties) !== JSON.stringify(properties)) {
      setProperties(convertedProperties);
    } else {
        console.log('数据没有变化，跳过更新');
      }
    } else {
      console.log('没有有效的properties，检查是否需要清空数据');
      // 只有在明确需要清空时才设置为空数组
      // 如果templateContent完全无效且当前没有数据，才清空
      if (!templateContent || (!templateContent.id && !templateContent.properties && properties.length === 0)) {
        console.log('templateContent完全无效且当前无数据，设置为空数组');
      setProperties([]);
      } else {
        console.log('保持现有数据，不清空');
    }
    }
  }, [templateContent?.id, templateContent?.name, templateContent?.properties, isEditing]);

  const handleAddProperty = () => {
    setEditingProperty(null);
    form.resetFields();
    setSelectedSapFields([]);
    setActiveTab('manual');
    setModalVisible(true);
  };

  // 处理SAP字段选择
  const handleSapFieldsSelected = (fields: SapField[]) => {
    setSelectedSapFields(fields);
  };

  // 从SAP字段创建属性
  const createPropertiesFromSapFields = () => {
    if (selectedSapFields.length === 0) {
      message.warning(t('templates.visual.selectSapFirst'));
      return;
    }

    const newProperties: TemplateProperty[] = selectedSapFields.map(field => ({
      uid: generateUid(),
      name: field.AliasID,
      type: mapSapTypeToSchemaType(field.FieldType),
      description: field.Descr || '',
      isRequired: field.NotNull === 'Y',
      children: [],
      isExpanded: false,
    }));

    const updatedProperties = [...properties, ...newProperties];
    setProperties(updatedProperties);
    updateTemplateContent(updatedProperties);
    setModalVisible(false);
    setSelectedSapFields([]);
    message.success(t('templates.visual.addedProps', { count: newProperties.length }));
  };

  // 映射SAP字段类型到Schema类型
  const mapSapTypeToSchemaType = (sapType: string): string => {
    const typeMap: { [key: string]: string } = {
      'db_Alpha': 'string',
      'db_Memo': 'string',
      'db_Text': 'string',
      'db_Char': 'string',
      'db_Numeric': 'number',
      'db_Float': 'number',
      'db_Double': 'number',
      'db_Date': 'date',
      'db_Time': 'string',
      'db_Binary': 'string',
      'db_Image': 'string',
      'db_Link': 'string',
      'db_Object': 'object',
    };
    return typeMap[sapType] || 'string';
  };

  // 处理子属性SAP字段选择
  const handleChildSapFieldsSelected = (fields: SapField[]) => {
    setChildSelectedSapFields(fields);
  };

  // 从SAP字段创建子属性
  const createChildPropertiesFromSapFields = () => {
    if (childSelectedSapFields.length === 0) {
      message.warning(t('templates.visual.selectSapFirst'));
      return;
    }

    const newChildProperties: TemplateProperty[] = childSelectedSapFields.map(field => ({
      uid: generateUid(),
      name: field.AliasID,
      type: mapSapTypeToSchemaType(field.FieldType),
      description: field.Descr || '',
      isRequired: field.NotNull === 'Y',
      children: [],
      isExpanded: false,
    }));

    const newProperties = properties.map(prop => {
      if (prop.name === parentPropertyName) {
        return {
          ...prop,
          children: [...(prop.children || []), ...newChildProperties],
          isExpanded: true, // 添加子属性后保持展开状态
        };
      }
      return prop;
    });
    
    setProperties(newProperties);
    updateTemplateContent(newProperties);
    setChildModalVisible(false);
    setChildSelectedSapFields([]);
    message.success(`成功为 ${parentPropertyName} 添加 ${newChildProperties.length} 个子属性`);
  };



  const handleDeleteProperty = (index: number) => {
    const newProperties = properties.filter((_, i) => i !== index);
    setProperties(newProperties);
    updateTemplateContent(newProperties);
  };

  const addNestedProperty = (parentPropertyName: string) => {
    console.log('添加子属性:', {
      parentPropertyName: parentPropertyName,
      properties: properties
    });
    
    // 打开子属性选择模态框
    setParentPropertyName(parentPropertyName);
    setChildSelectedSapFields([]);
    setChildActiveTab('manual');
    setChildModalVisible(true);
  };

  // 手动添加子属性
  const addManualNestedProperty = (parentPropertyName: string) => {
    const newProperties = properties.map(prop => {
      if (prop.name === parentPropertyName) {
        const newChild: TemplateProperty = {
          uid: generateUid(),
          name: `field_${(prop.children?.length || 0) + 1}`,
          type: 'string',
          description: '',
          isRequired: false,
          itemsType: undefined,
          itemsDescription: '',
          children: [],
          isExpanded: false,
        };
        
        return {
          ...prop,
          children: [...(prop.children || []), newChild],
          isExpanded: true, // 添加子属性后保持展开状态
        };
      }
      return prop;
    });
    
    setProperties(newProperties);
    updateTemplateContent(newProperties);
    setChildModalVisible(false);
  };



  // 检查array的items类型是否被锁定（有子属性时不能切换）
  const isItemsTypeLocked = (property: TemplateProperty): boolean => {
    if (property.type === 'array' && property.itemsType === 'object') {
      return !!(property.children && property.children.length > 0);
    }
    return false;
  };

  // 当顶层属性类型变更时的处理
  const onTopLevelTypeChange = (propertyName: string, newType: string) => {
    console.log('类型变更开始:', {
      propertyName: propertyName,
      newType: newType,
      currentProperties: properties
    });
    
    const newProperties = properties.map(prop => {
      console.log('处理属性:', {
        propName: prop.name,
        targetPropertyName: propertyName,
        isMatch: prop.name === propertyName,
        currentType: prop.type,
        newType: newType
      });
      
      if (prop.name === propertyName) {
        console.log('找到匹配的属性，开始处理类型切换');
        
        // 检查类型锁定：如果当前是array且item_type是object且有子属性，不允许切换类型
        if (prop.type === 'array' && prop.itemsType === 'object' && prop.children && prop.children.length > 0) {
          console.log('类型锁定：array的item_type是object且有子属性，不允许切换类型');
      message.warning(t('templates.visual.arrayObjectLock'));
          return prop; // 返回原属性，不进行类型切换
        }
        
        // 检查类型锁定：如果当前是object且有子属性，不允许切换类型
        if (prop.type === 'object' && prop.children && prop.children.length > 0) {
          console.log('类型锁定：object且有子属性，不允许切换类型');
          message.warning(t('templates.visual.objectHasChildrenLock'));
          return prop; // 返回原属性，不进行类型切换
        }
        
        const updatedProp = { ...prop, type: newType };
        console.log('创建updatedProp:', {
          originalProp: prop,
          updatedProp: updatedProp
        });
        
        if (newType === 'array') {
          // 切换为 array 时，设置默认的 itemsType
          if (!updatedProp.itemsType) {
            updatedProp.itemsType = 'string';
          }
          // 如果不是 object 类型，收起展开状态
          if (updatedProp.itemsType !== 'object') {
            updatedProp.isExpanded = false;
            updatedProp.children = [];
          }
        } else if (newType === 'object') {
          console.log('进入object类型处理分支');
          // 切换为 object 时，准备子属性结构
          console.log('切换为object类型:', {
            propertyName: prop.name,
            originalChildren: prop.children,
            originalChildrenLength: prop.children?.length,
            beforeUpdate: {
              children: updatedProp.children,
              isExpanded: updatedProp.isExpanded
            }
          });
          // 清空现有子属性，重新开始
          updatedProp.children = [];
          updatedProp.isExpanded = true; // 自动展开，显示添加子属性按钮
          console.log('object类型处理完成:', {
            afterUpdate: {
              children: updatedProp.children,
              isExpanded: updatedProp.isExpanded
            }
          });
        } else {
          // 其他类型时，清理子属性
          console.log('切换到非object类型，清理子属性:', {
            propertyName: prop.name,
            oldType: prop.type,
            newType: newType,
            oldChildren: prop.children,
            oldChildrenLength: prop.children?.length,
            oldIsExpanded: prop.isExpanded
          });
          updatedProp.children = [];
          updatedProp.isExpanded = false;
          updatedProp.itemsType = undefined;
          console.log('清理完成:', {
            newChildren: updatedProp.children,
            newIsExpanded: updatedProp.isExpanded,
            newItemsType: updatedProp.itemsType
          });
        }
        
        return updatedProp;
      }
      return prop;
    });
    
    console.log('类型变更完成:', {
      newProperties: newProperties,
      targetProperty: newProperties.find(p => p.name === propertyName)
    });
    
    setProperties(newProperties);
    
    // 添加延迟日志，检查状态是否正确设置
    setTimeout(() => {
      console.log('延迟检查状态:', {
        propertyName: propertyName,
        newType: newType,
        currentProperties: properties,
        newProperties: newProperties,
        targetProperty: newProperties.find(p => p.name === propertyName)
      });
    }, 200);
    
    // 延迟调用updateTemplateContent，确保状态更新完成
    setTimeout(() => {
      updateTemplateContent(newProperties);
    }, 100);
  };

  // 当 array 的 items 类型变更
  const onArrayItemsTypeChange = (propertyName: string, newItemsType: string) => {
    const newProperties = properties.map(prop => {
      if (prop.name === propertyName) {
        // 安全检查：如果有子属性，不允许切换到非object类型
        if (isItemsTypeLocked(prop) && newItemsType !== 'object') {
          message.warning(t('templates.visual.itemsTypeLock'));
          return prop;
        }
        
        const updatedProp = { ...prop, itemsType: newItemsType };
        
        if (newItemsType === 'object') {
          // 切换为 object 时，准备可扩展的结构
          updatedProp.children = updatedProp.children || [];
          updatedProp.isExpanded = true; // 自动展开，显示添加子属性按钮
        } else {
          // 非 object 时，移除可扩展结构
          updatedProp.children = [];
          updatedProp.isExpanded = false;
        }
        
        return updatedProp;
      }
      return prop;
    });
    
    setProperties(newProperties);
    updateTemplateContent(newProperties);
  };

  // 拖拽相关方法
  const onDragStart = (event: React.DragEvent, propertyName: string, index: number) => {
    setDragSource(propertyName);
    setDragSourceIndex(index);
    setIsDragging(true);
    
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', propertyName);
    
    // 使用整行作为拖拽预览
    const target = event.target as HTMLElement;
    const rowEl = target.closest('tr') as HTMLElement | null;
    if (rowEl && event.dataTransfer && event.dataTransfer.setDragImage) {
      const rect = rowEl.getBoundingClientRect();
      const clone = rowEl.cloneNode(true) as HTMLElement;
      clone.style.width = `${rect.width}px`;
      clone.style.height = `${rect.height}px`;
      clone.style.position = 'absolute';
      clone.style.top = '-10000px';
      clone.style.left = '-10000px';
      clone.style.pointerEvents = 'none';
      document.body.appendChild(clone);
      try {
        event.dataTransfer.setDragImage(clone, 16, 16);
      } finally {
        setTimeout(() => {
          if (clone && clone.parentNode) {
            clone.parentNode.removeChild(clone);
          }
        }, 0);
      }
    }
  };

  const onDragOver = (event: React.DragEvent, index: number) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    
    if (dragSourceIndex !== index) {
      setDragOverIndex(index);
    }
  };

  const onDrop = (event: React.DragEvent, targetIndex: number) => {
    event.preventDefault();
    
    if (dragSource === null || dragSourceIndex === -1) return;
    
    const sourceIndex = dragSourceIndex;
    if (sourceIndex === targetIndex) return; // 相同位置不需要移动
    
    // 重新排序属性
    reorderProperties(sourceIndex, targetIndex);
    
    // 重置拖拽状态
    resetDragState();
  };

  const onDragEnter = (event: React.DragEvent, index: number) => {
    event.preventDefault();
    if (isDragging && dragSourceIndex !== index) {
      setDragOverIndex(index);
    }
  };

  const onDragLeave = (event: React.DragEvent) => {
    // 检查是否真的离开了行，而不是进入子元素
    if (!event.currentTarget.contains(event.relatedTarget as Node)) {
      setDragOverIndex(-1);
    }
  };

  // 重新排序属性
  const reorderProperties = (fromIndex: number, toIndex: number) => {
    const newProperties = [...properties];
    const movedProperty = newProperties[fromIndex];
    
    // 移除源位置的属性
    newProperties.splice(fromIndex, 1);
    // 在目标位置插入属性
    newProperties.splice(toIndex, 0, movedProperty);
    
    setProperties(newProperties);
    updateTemplateContent(newProperties);
  };

  // 重置拖拽状态
  const resetDragState = () => {
    setDragSource(null);
    setDragSourceIndex(-1);
    setDragOverIndex(-1);
    setIsDragging(false);
    
    // 恢复所有行的透明度
    const rows = document.querySelectorAll('tbody tr[draggable="true"]');
    rows.forEach(row => {
      (row as HTMLElement).style.opacity = '1';
    });
  };

  // 子级拖拽处理
  const onChildDragStart = (event: React.DragEvent, parentName: string, index: number) => {
    setChildDragParentName(parentName);
    setChildDragSourceIndex(index);
    setChildIsDragging(true);
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', `${parentName}:${index}`);
    // 整块预览
    const target = event.target as HTMLElement;
    const block = target.closest('[data-child-row="true"]') as HTMLElement | null;
    if (block && event.dataTransfer && event.dataTransfer.setDragImage) {
      const rect = block.getBoundingClientRect();
      const clone = block.cloneNode(true) as HTMLElement;
      clone.style.width = `${rect.width}px`;
      clone.style.height = `${rect.height}px`;
      clone.style.position = 'absolute';
      clone.style.top = '-10000px';
      clone.style.left = '-10000px';
      clone.style.pointerEvents = 'none';
      document.body.appendChild(clone);
      try {
        event.dataTransfer.setDragImage(clone, 16, 16);
      } finally {
        setTimeout(() => {
          if (clone && clone.parentNode) clone.parentNode.removeChild(clone);
        }, 0);
      }
    }
  };

  const onChildDragOver = (event: React.DragEvent, parentName: string, index: number) => {
    event.preventDefault();
    if (!childIsDragging || childDragParentName !== parentName) return;
    if (childDragSourceIndex !== index) {
      setChildDragOverIndex(index);
    }
  };

  const onChildDrop = (event: React.DragEvent, parentName: string, targetIndex: number) => {
    event.preventDefault();
    if (!childIsDragging || childDragParentName !== parentName) return;
    const sourceIndex = childDragSourceIndex;
    if (sourceIndex === targetIndex || sourceIndex === -1) return;
    reorderChildProperties(parentName, sourceIndex, targetIndex);
    resetChildDragState();
  };

  const resetChildDragState = () => {
    setChildDragParentName(null);
    setChildDragSourceIndex(-1);
    setChildDragOverIndex(-1);
    setChildIsDragging(false);
  };

  const reorderChildProperties = (parentName: string, fromIndex: number, toIndex: number) => {
    const newProperties = properties.map(prop => {
      if (prop.name === parentName) {
        const childList = [...(prop.children || [])];
        const moved = childList.splice(fromIndex, 1)[0];
        childList.splice(toIndex, 0, moved);
        return { ...prop, children: childList };
      }
      return prop;
    });
    setProperties(newProperties);
    updateTemplateContent(newProperties);
  };

  // 孙级拖拽处理
  const onGrandDragStart = (event: React.DragEvent, parentName: string, childName: string, index: number) => {
    setGrandDragParentName(parentName);
    setGrandDragChildName(childName);
    setGrandDragSourceIndex(index);
    setGrandIsDragging(true);
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', `${parentName}:${childName}:${index}`);
    // 整块预览
    const target = event.target as HTMLElement;
    const block = target.closest('[data-grand-row="true"]') as HTMLElement | null;
    if (block && event.dataTransfer && event.dataTransfer.setDragImage) {
      const rect = block.getBoundingClientRect();
      const clone = block.cloneNode(true) as HTMLElement;
      clone.style.width = `${rect.width}px`;
      clone.style.height = `${rect.height}px`;
      clone.style.position = 'absolute';
      clone.style.top = '-10000px';
      clone.style.left = '-10000px';
      clone.style.pointerEvents = 'none';
      document.body.appendChild(clone);
      try {
        event.dataTransfer.setDragImage(clone, 16, 16);
      } finally {
        setTimeout(() => {
          if (clone && clone.parentNode) clone.parentNode.removeChild(clone);
        }, 0);
      }
    }
  };

  const onGrandDragOver = (event: React.DragEvent, parentName: string, childName: string, index: number) => {
    event.preventDefault();
    if (!grandIsDragging || grandDragParentName !== parentName || grandDragChildName !== childName) return;
    if (grandDragSourceIndex !== index) {
      setGrandDragOverIndex(index);
    }
  };

  const onGrandDrop = (event: React.DragEvent, parentName: string, childName: string, targetIndex: number) => {
    event.preventDefault();
    if (!grandIsDragging || grandDragParentName !== parentName || grandDragChildName !== childName) return;
    const sourceIndex = grandDragSourceIndex;
    if (sourceIndex === targetIndex || sourceIndex === -1) return;
    reorderGrandChildren(parentName, childName, sourceIndex, targetIndex);
    resetGrandDragState();
  };

  const resetGrandDragState = () => {
    setGrandDragParentName(null);
    setGrandDragChildName(null);
    setGrandDragSourceIndex(-1);
    setGrandDragOverIndex(-1);
    setGrandIsDragging(false);
  };

  const reorderGrandChildren = (parentName: string, childName: string, fromIndex: number, toIndex: number) => {
    const newProperties = properties.map(prop => {
      if (prop.name === parentName) {
        const newChildren = (prop.children || []).map(c => {
          if (c.name === childName) {
            const gcList = [...(c.children || [])];
            const moved = gcList.splice(fromIndex, 1)[0];
            gcList.splice(toIndex, 0, moved);
            return { ...c, children: gcList };
          }
          return c;
        });
        return { ...prop, children: newChildren };
      }
      return prop;
    });
    setProperties(newProperties);
    updateTemplateContent(newProperties);
  };

  const handleSaveProperty = async () => {
    try {
      const values = await form.validateFields();
      const newProperty: TemplateProperty = {
        uid: editingProperty?.uid || generateUid(),
        name: values.name,
        type: values.type,
        description: values.description || '',
        isRequired: values.isRequired || false,
        itemsType: values.type === 'array' ? values.itemsType : undefined,
        itemsDescription: values.type === 'array' ? values.itemsDescription : undefined,
      };

      let newProperties: TemplateProperty[];
      if (editingProperty) {
        // 编辑模式
        const index = properties.findIndex(p => p.uid === editingProperty.uid);
        newProperties = [...properties];
        newProperties[index] = newProperty;
      } else {
        // 新增模式
        newProperties = [...properties, newProperty];
      }

      setProperties(newProperties);
      updateTemplateContent(newProperties);
      setModalVisible(false);
      form.resetFields();
    } catch (error) {
      console.error('保存属性失败:', error);
    }
  };

  const updateTemplateContent = (newProperties: TemplateProperty[]) => {
    console.log('updateTemplateContent调用:', {
      newProperties: newProperties,
      newPropertiesLength: newProperties.length,
      newPropertiesNames: newProperties.map(p => p.name)
    });
    
    const dataProperties: any = {};
    const required: string[] = [];

    newProperties.forEach(prop => {
      const propertyDef: any = {
        type: prop.type,
        description: prop.description,
      };

      if (prop.type === 'array' && prop.itemsType) {
        propertyDef.items = {
          type: prop.itemsType,
          description: prop.itemsDescription,
        };

        if (prop.itemsType === 'object' && prop.children) {
          const childProperties: any = {};
          const childRequired: string[] = [];

          prop.children.forEach(child => {
            childProperties[child.name] = {
              type: child.type,
              description: child.description,
            };
            if (child.isRequired) {
              childRequired.push(child.name);
            }
          });

          propertyDef.items.properties = childProperties;
          if (childRequired.length > 0) {
            propertyDef.items.required = childRequired;
          }
        }
      } else if (prop.type === 'object' && prop.children) {
        const childProperties: any = {};
        const childRequired: string[] = [];

        prop.children.forEach(child => {
          const childProperty: any = {
            type: child.type,
            description: child.description,
          };
          
          // 如果子属性是 Array 类型且有 itemsType，添加 items 定义
          if (child.type === 'array' && child.itemsType) {
            childProperty.items = {
              type: child.itemsType,
              description: child.itemsDescription || '',
            };
            
            // 如果 Array 的 items 是 object 类型且有子属性，递归处理
            if (child.itemsType === 'object' && child.children) {
              const grandChildProperties: any = {};
              const grandChildRequired: string[] = [];
              
              child.children.forEach(grandChild => {
                grandChildProperties[grandChild.name] = {
                  type: grandChild.type,
                  description: grandChild.description,
                };
                if (grandChild.isRequired) {
                  grandChildRequired.push(grandChild.name);
                }
              });
              
              childProperty.items.properties = grandChildProperties;
              if (grandChildRequired.length > 0) {
                childProperty.items.required = grandChildRequired;
              }
            }
          }
          // 如果子属性是 object 类型且有子属性，递归处理
          else if (child.type === 'object' && child.children) {
            const grandChildProperties: any = {};
            const grandChildRequired: string[] = [];
            
            child.children.forEach(grandChild => {
              grandChildProperties[grandChild.name] = {
                type: grandChild.type,
                description: grandChild.description,
              };
              if (grandChild.isRequired) {
                grandChildRequired.push(grandChild.name);
              }
            });
            
            childProperty.properties = grandChildProperties;
            if (grandChildRequired.length > 0) {
              childProperty.required = grandChildRequired;
            }
          }
          
          childProperties[child.name] = childProperty;
          if (child.isRequired) {
            childRequired.push(child.name);
          }
        });

        propertyDef.properties = childProperties;
        if (childRequired.length > 0) {
          propertyDef.required = childRequired;
        }
      }

      dataProperties[prop.name] = propertyDef;
      if (prop.isRequired) {
        required.push(prop.name);
      }
    });

    // 直接使用dataProperties作为properties，不创建data层
    const newTemplateContent = {
      ...templateContent,
      properties: dataProperties,
      required: required
    };

    console.log('构建的请求数据:', {
      newTemplateContent: newTemplateContent,
      dataProperties: dataProperties,
      dataPropertiesKeys: Object.keys(dataProperties),
      required: required
    });
    console.log('完整的请求数据:', {
      templateContent: newTemplateContent,
      templateContentString: JSON.stringify(newTemplateContent, null, 2)
    });

    onTemplateChange(newTemplateContent);
  };



  const renderChildren = (children: TemplateProperty[], parentPropertyName: string) => {
    console.log('renderChildren函数开始执行:', {
      parentPropertyName: parentPropertyName,
      children: children,
      childrenLength: children?.length,
      hasChildren: !(!children || children.length === 0),
      childrenType: typeof children,
      childrenIsArray: Array.isArray(children)
    });
    
    // 如果没有子属性，只显示添加按钮
    if (!children || children.length === 0) {
      console.log('没有子属性，显示添加按钮');
      return (
        <div style={{ marginLeft: 20, marginTop: 8 }}>
          <Button 
            type="dashed" 
            size="small"
            onClick={() => {
              console.log('添加子属性按钮点击（无子属性）:', parentPropertyName);
              addNestedProperty(parentPropertyName);
            }}
            style={{ width: '100%' }}
          >
            + 添加 {parentPropertyName} 子属性
          </Button>
        </div>
      );
    }

    console.log('renderChildren显示子属性列表:', {
      parentPropertyName: parentPropertyName,
      childrenCount: children.length,
      willShowAddButton: true
    });

    return (
      <div style={{ marginLeft: 20, marginTop: 8 }}>
        {children.map((child, index) => (
          <div key={child.uid} data-child-row="true" style={{ 
            padding: '8px', 
            background: '#f5f5f5', 
            borderRadius: 4, 
            marginBottom: 4,
            border: '1px solid #d9d9d9'
          }}>
            <Row gutter={8} align="middle" wrap={false}>
              <Col span={2}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  {/* 拖拽手柄 - 始终显示 */}
                  <span
                    draggable={true}
                    onDragStart={(e) => onChildDragStart(e as any, parentPropertyName, index)}
                    onDragOver={(e) => onChildDragOver(e as any, parentPropertyName, index)}
                    onDrop={(e) => onChildDrop(e as any, parentPropertyName, index)}
                    style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 48, height: 48 }}
                  >
                    <HolderOutlined style={{ color: '#999', cursor: 'move', fontSize: 24 }} />
                  </span>
                  
                  {/* 展开按钮或减号 - 参考HTML实现 */}
                  {(() => {
                    const hasChildren = (child.type === 'array' && child.itemsType === 'object') || child.type === 'object';
                    
                    // 不管hasChildren是否为true，都要显示按钮
                    return (
                      <Button
                        type="text"
                        size="small"
                        onClick={() => {
                          if (hasChildren) {
                            // 如果有子节点，切换展开状态
                            const newProperties = properties.map(prop => {
                              if (prop.name === parentPropertyName) {
                                return {
                                  ...prop,
                                  children: (prop.children || []).map(c => 
                                    c.name === child.name ? { ...c, isExpanded: !c.isExpanded } : c
                                  )
                                };
                              }
                              return prop;
                            });
                            setProperties(newProperties);
                          }
                          // 如果没有子节点，点击按钮不做任何操作
                        }}
                        style={{ 
                          padding: 0, 
                          width: 32, 
                          height: 32, 
                          fontSize: '18px',
                          fontWeight: 'bold',
                          color: hasChildren ? '#1890ff' : '#999',
                          lineHeight: '32px'
                        }}
                      >
                        {hasChildren ? (child.isExpanded ? '−' : '+') : '−'}
                      </Button>
                    );
                  })()}
                </div>
              </Col>
              <Col span={4}>
                <Input
                  size="small"
                  value={child.name}
                  onChange={(e) => {
                    const newProperties = properties.map(prop => {
                      if (prop.name === parentPropertyName) {
                        return {
                          ...prop,
                          children: (prop.children || []).map(c => 
                            c.name === child.name ? { ...c, name: e.target.value } : c
                          )
                        };
                      }
                      return prop;
                    });
                    setProperties(newProperties);
                    updateTemplateContent(newProperties);
                  }}
                  placeholder={t('templates.visual.enterFieldName')}
                />
              </Col>
              <Col span={4}>
                <div>
                  <Select
                    size="small"
                    value={child.type}
                    onChange={(value) => {
                      // 检查子属性的类型锁定：如果当前是array且item_type是object且有子属性，不允许切换类型
                      if (child.type === 'array' && child.itemsType === 'object' && child.children && child.children.length > 0) {
                        console.log('子属性类型锁定：array的item_type是object且有子属性，不允许切换类型');
          message.warning(t('templates.visual.arrayObjectLock'));
                        return; // 不进行类型切换
                      }
                      
                      // 检查子属性的类型锁定：如果当前是object且有子属性，不允许切换类型
                      if (child.type === 'object' && child.children && child.children.length > 0) {
                        console.log('子属性类型锁定：object且有子属性，不允许切换类型');
                        message.warning(t('templates.visual.objectHasChildrenLock'));
                        return; // 不进行类型切换
                      }
                      
                      const newProperties = properties.map(prop => {
                        if (prop.name === parentPropertyName) {
                          return {
                            ...prop,
                            children: (prop.children || []).map(c => 
                              c.name === child.name ? { ...c, type: value } : c
                            )
                          };
                        }
                        return prop;
                      });
                      setProperties(newProperties);
                      updateTemplateContent(newProperties);
                    }}
                    style={{ width: '100%', marginBottom: child.type === 'array' ? 4 : 0 }}
                  >
                    <Select.Option value="string">string</Select.Option>
                    <Select.Option value="number">number</Select.Option>
                    <Select.Option value="boolean">boolean</Select.Option>
                    <Select.Option value="date">date</Select.Option>
                    <Select.Option value="array">array</Select.Option>
                    <Select.Option value="object">object</Select.Option>
                  </Select>
                  
                  {child.type === 'array' && (
                    <div>
                      <Text type="secondary" style={{ fontSize: '10px' }}>{t('templates.visual.itemsType')}:</Text>
                      <Select
                        size="small"
                        value={child.itemsType || 'string'}
                        onChange={(value) => {
                          // 检查子属性的itemsType锁定：如果当前是array且item_type是object且有子属性，不允许切换
                          if (child.type === 'array' && child.itemsType === 'object' && child.children && child.children.length > 0) {
                            console.log('子属性itemsType锁定：array的item_type是object且有子属性，不允许切换');
                          message.warning(t('templates.visual.itemsTypeLock'));
                            return; // 不进行切换
                          }
                          
                          const newProperties = properties.map(prop => {
                            if (prop.name === parentPropertyName) {
                              return {
                                ...prop,
                                children: (prop.children || []).map(c => 
                                  c.name === child.name ? { ...c, itemsType: value } : c
                                )
                              };
                            }
                            return prop;
                          });
                          setProperties(newProperties);
                          updateTemplateContent(newProperties);
                        }}
                        disabled={(child.type === 'array' && child.itemsType === 'object' && child.children && child.children.length > 0) || ((child.type as string) === 'object' && child.children && child.children.length > 0)}
                        style={{ 
                          width: '100%', 
                          marginTop: 2,
                          opacity: ((child.type === 'array' && child.itemsType === 'object' && child.children && child.children.length > 0) || ((child.type as string) === 'object' && child.children && child.children.length > 0)) ? 0.5 : 1
                        }}
                      >
                        <Select.Option value="string">string</Select.Option>
                        <Select.Option value="number">number</Select.Option>
                        <Select.Option value="boolean">boolean</Select.Option>
                        <Select.Option value="date">date</Select.Option>
                        <Select.Option value="object">object</Select.Option>
                      </Select>
                      {child.type === 'array' && child.itemsType === 'object' && child.children && child.children.length > 0 && (
                        <Text type="warning" style={{ fontSize: '10px' }}>
                          {t('templates.visual.itemsTypeLockShort')}
                        </Text>
                      )}
                      {(child.type as string) === 'object' && child.children && child.children.length > 0 && (
                        <Text type="warning" style={{ fontSize: '10px' }}>
                          {t('templates.visual.typeLockShort')}
                        </Text>
                      )}
                    </div>
                  )}
                </div>
              </Col>
              <Col span={10}>
                <Input.TextArea
                  size="small"
                  value={child.description}
                  onChange={(e) => {
                    const newProperties = properties.map(prop => {
                      if (prop.name === parentPropertyName) {
                        return {
                          ...prop,
                          children: (prop.children || []).map(c => 
                            c.name === child.name ? { ...c, description: e.target.value } : c
                          )
                        };
                      }
                      return prop;
                    });
                    setProperties(newProperties);
                    updateTemplateContent(newProperties);
                  }}
                  placeholder={t('templates.visual.enterDescription')}
                  rows={2}
                  style={{ resize: 'vertical' }}
                />
              </Col>
              <Col span={2}>
                <input
                  type="checkbox"
                  checked={child.isRequired}
                  onChange={(e) => {
                    const newProperties = properties.map(prop => {
                      if (prop.name === parentPropertyName) {
                        return {
                          ...prop,
                          children: (prop.children || []).map(c => 
                            c.name === child.name ? { ...c, isRequired: e.target.checked } : c
                          )
                        };
                      }
                      return prop;
                    });
                    setProperties(newProperties);
                    updateTemplateContent(newProperties);
                  }}
                />
                <span style={{ marginLeft: 4, fontSize: '12px' }}>{t('templates.visual.colRequired')}</span>
              </Col>
              <Col span={2}>
                <Button
                  type="text"
                  danger
                  size="small"
                  icon={<DeleteOutlined />}
                  onClick={() => {
                    const newProperties = properties.map(prop => {
                      if (prop.name === parentPropertyName) {
                        return {
                          ...prop,
                          children: (prop.children || []).filter(c => c.name !== child.name)
                        };
                      }
                      return prop;
                    });
                    setProperties(newProperties);
                    updateTemplateContent(newProperties);
                  }}
                />
              </Col>
            </Row>
            
            {/* 显示子属性的子属性（第二层级） */}
            {child.isExpanded && (
              <div style={{ marginTop: 8, marginLeft: 20 }}>
                {/* 如果没有子属性，显示添加按钮 */}
                {(!child.children || child.children.length === 0) ? (
                  <Button 
                    type="dashed" 
                    size="small"
                    onClick={() => {
                      const newGrandChild: TemplateProperty = {
                        uid: generateUid(),
                        name: `field_${(child.children?.length || 0) + 1}`,
                        type: 'string',
                        description: '',
                        isRequired: false,
                        itemsType: undefined,
                        itemsDescription: '',
                        children: [],
                        isExpanded: false,
                      };
                      
                      const newProperties = properties.map(prop => {
                        if (prop.name === parentPropertyName) {
                          return {
                            ...prop,
                            children: (prop.children || []).map(c => 
                              c.name === child.name ? {
                                ...c,
                                children: [...(c.children || []), newGrandChild]
                              } : c
                            )
                          };
                        }
                        return prop;
                      });
                      setProperties(newProperties);
                      updateTemplateContent(newProperties);
                    }}
                    style={{ width: '100%' }}
                  >
                    {t('templates.visual.addChildFor', { name: child.name })}
                  </Button>
                ) : (
                  <>
                    {/* 显示子属性列表 */}
                    {child.children.map((grandChild, grandIndex) => (
                  <div key={grandChild.uid} data-grand-row="true" style={{ 
                    padding: '6px', 
                    background: '#fafafa', 
                    borderRadius: 4, 
                    marginBottom: 4,
                    border: '1px solid #e8e8e8'
                  }}>
                  <Row gutter={8} align="middle" wrap={false}>
              <Col span={2}>
                <span
                  draggable={true}
                  onDragStart={(e) => onGrandDragStart(e as any, parentPropertyName, child.name, grandIndex)}
                  onDragOver={(e) => onGrandDragOver(e as any, parentPropertyName, child.name, grandIndex)}
                  onDrop={(e) => onGrandDrop(e as any, parentPropertyName, child.name, grandIndex)}
                          style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 40, height: 40 }}
                >
                          <HolderOutlined style={{ color: '#999', cursor: 'move', fontSize: 20 }} />
                </span>
              </Col>
                      <Col span={4}>
                        <Input
                          size="small"
                          value={grandChild.name}
                          onChange={(e) => {
                            const newProperties = properties.map(prop => {
                              if (prop.name === parentPropertyName) {
                                return {
                                  ...prop,
                                  children: (prop.children || []).map(c => 
                                    c.name === child.name ? {
                                      ...c,
                                      children: (c.children || []).map(gc => 
                                        gc.name === grandChild.name ? { ...gc, name: e.target.value } : gc
                                      )
                                    } : c
                                  )
                                };
                              }
                              return prop;
                            });
                            setProperties(newProperties);
                            updateTemplateContent(newProperties);
                          }}
                          placeholder={t('templates.visual.enterFieldName')}
                        />
                      </Col>
                      <Col span={4}>
                        <Select
                          size="small"
                          value={grandChild.type}
                          onChange={(value) => {
                            // 检查孙属性的类型锁定：如果当前是array且item_type是object且有子属性，不允许切换类型
                            if (grandChild.type === 'array' && grandChild.itemsType === 'object' && grandChild.children && grandChild.children.length > 0) {
                              console.log('孙属性类型锁定：array的item_type是object且有子属性，不允许切换类型');
                              message.warning(t('templates.visual.arrayObjectLock'));
                               return; // 不进行类型切换
                            }
                            
                            const newProperties = properties.map(prop => {
                              if (prop.name === parentPropertyName) {
                                return {
                                  ...prop,
                                  children: (prop.children || []).map(c => 
                                    c.name === child.name ? {
                                      ...c,
                                      children: (c.children || []).map(gc => 
                                        gc.name === grandChild.name ? { ...gc, type: value } : gc
                                      )
                                    } : c
                                  )
                                };
                              }
                              return prop;
                            });
                            setProperties(newProperties);
                            updateTemplateContent(newProperties);
                          }}
                          style={{ width: '100%' }}
                        >
                          <Select.Option value="string">string</Select.Option>
                          <Select.Option value="number">number</Select.Option>
                          <Select.Option value="boolean">boolean</Select.Option>
                          <Select.Option value="date">date</Select.Option>
                          <Select.Option value="array">array</Select.Option>
                          <Select.Option value="object">object</Select.Option>
                        </Select>
                      </Col>
                      <Col span={10}>
                        <Input.TextArea
                          size="small"
                          value={grandChild.description}
                          onChange={(e) => {
                            const newProperties = properties.map(prop => {
                              if (prop.name === parentPropertyName) {
                                return {
                                  ...prop,
                                  children: (prop.children || []).map(c => 
                                    c.name === child.name ? {
                                      ...c,
                                      children: (c.children || []).map(gc => 
                                        gc.name === grandChild.name ? { ...gc, description: e.target.value } : gc
                                      )
                                    } : c
                                  )
                                };
                              }
                              return prop;
                            });
                            setProperties(newProperties);
                            updateTemplateContent(newProperties);
                          }}
                          placeholder={t('templates.visual.enterDescription')}
                          rows={2}
                          style={{ resize: 'vertical' }}
                        />
                      </Col>
                      <Col span={2}>
                        <input
                          type="checkbox"
                          checked={grandChild.isRequired}
                          onChange={(e) => {
                            const newProperties = properties.map(prop => {
                              if (prop.name === parentPropertyName) {
                                return {
                                  ...prop,
                                  children: (prop.children || []).map(c => 
                                    c.name === child.name ? {
                                      ...c,
                                      children: (c.children || []).map(gc => 
                                        gc.name === grandChild.name ? { ...gc, isRequired: e.target.checked } : gc
                                      )
                                    } : c
                                  )
                                };
                              }
                              return prop;
                            });
                            setProperties(newProperties);
                            updateTemplateContent(newProperties);
                          }}
                        />
                        <span style={{ marginLeft: 4, fontSize: '12px' }}>{t('templates.visual.colRequired')}</span>
                      </Col>
                      <Col span={2}>
                        <Button
                          type="text"
                          danger
                          size="small"
                          icon={<DeleteOutlined />}
                          onClick={() => {
                            const newProperties = properties.map(prop => {
                              if (prop.name === parentPropertyName) {
                                return {
                                  ...prop,
                                  children: (prop.children || []).map(c => 
                                    c.name === child.name ? {
                                      ...c,
                                      children: (c.children || []).filter(gc => gc.name !== grandChild.name)
                                    } : c
                                  )
                                };
                              }
                              return prop;
                            });
                            setProperties(newProperties);
                            updateTemplateContent(newProperties);
                          }}
                        />
                      </Col>
                    </Row>
          </div>
        ))}
                    
                    {/* 底部添加子属性按钮 */}
                    <Button 
                      type="dashed" 
                      size="small"
                      onClick={() => {
                        const newGrandChild: TemplateProperty = {
                          uid: generateUid(),
                          name: `field_${(child.children?.length || 0) + 1}`,
                          type: 'string',
                          description: '',
                          isRequired: false,
                          itemsType: undefined,
                          itemsDescription: '',
                          children: [],
                          isExpanded: false,
                        };
                        
                        const newProperties = properties.map(prop => {
                          if (prop.name === parentPropertyName) {
                            return {
                              ...prop,
                              children: (prop.children || []).map(c => 
                                c.name === child.name ? {
                                  ...c,
                                  children: [...(c.children || []), newGrandChild]
                                } : c
                              )
                            };
                          }
                          return prop;
                        });
                        setProperties(newProperties);
                        updateTemplateContent(newProperties);
                      }}
                      style={{ width: '100%', marginTop: 8 }}
                    >
                      {t('templates.visual.addChildFor', { name: child.name })}
                    </Button>
                  </>
                )}
              </div>
            )}
          </div>
        ))}
        <Button 
          type="dashed" 
          size="small"
          onClick={() => {
            console.log('底部添加子属性按钮点击（有子属性）:', parentPropertyName);
            addNestedProperty(parentPropertyName);
          }}
          style={{ width: '100%', marginTop: 8 }}
        >
          {t('templates.visual.addChildFor', { name: parentPropertyName })}
        </Button>
      </div>
    );
  };



  // 调试：显示所有属性状态
  console.log('所有属性状态:', properties.map(prop => ({
    name: prop.name,
    type: prop.type,
    hasChildren: prop.children?.length || 0,
    isExpanded: prop.isExpanded
  })));

  const columns = [
    {
      title: t('templates.visual.colDrag'),
      key: 'drag',
      width: '10%',
      align: 'center' as const,
      render: (_: any, record: TemplateProperty, index: number) => {
        const hasChildren = (record.type === 'array' && record.itemsType === 'object') || record.type === 'object';
        
        console.log('拖拽列渲染:', {
          recordName: record.name,
          recordType: record.type,
          recordItemsType: record.itemsType,
          hasChildren: hasChildren,
          isExpanded: record.isExpanded
        });
        
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            {/* 拖拽手柄 - 始终显示 */}
            <span
              draggable={true}
              onDragStart={(e) => onDragStart(e as any, record.uid, index)}
              onDragOver={(e) => onDragOver(e as any, index)}
              onDrop={(e) => onDrop(e as any, index)}
              onDragEnter={(e) => onDragEnter(e as any, index)}
              style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 48, height: 48 }}
            >
              <HolderOutlined 
                style={{ cursor: 'move', color: '#999', fontSize: 24 }}
              />
            </span>
            
            {/* 展开按钮或减号 - 参考HTML实现 */}
            {hasChildren ? (
              <Button
                type="text"
                size="small"
                onClick={() => {
                  console.log('展开按钮点击:', {
                    recordName: record.name,
                    recordType: record.type,
                    recordItemsType: record.itemsType,
                    currentExpanded: record.isExpanded,
                    hasChildren: hasChildren,
                    actualChildren: record.children,
                    actualChildrenLength: record.children?.length || 0
                  });
                  
                  const newProperties = properties.map(prop => 
                    prop.name === record.name ? { ...prop, isExpanded: !prop.isExpanded } : prop
                  );
                  
                  console.log('展开按钮点击后状态更新:', {
                    recordName: record.name,
                    oldExpanded: record.isExpanded,
                    newExpanded: !record.isExpanded,
                    newProperties: newProperties.find(p => p.name === record.name),
                    newTargetProperty: newProperties.find(p => p.name === record.name)
                  });
                  
                  setProperties(newProperties);
                }}
                style={{ 
                  padding: 0, 
                  width: 40, 
                  height: 40, 
                  fontSize: '20px',
                  fontWeight: 'bold',
                  color: '#1890ff',
                  lineHeight: '40px'
                }}
              >
                {record.isExpanded ? '−' : '+'}
              </Button>
            ) : (
              <span style={{ 
                width: 40, 
                height: 40, 
                display: 'inline-block',
                fontSize: '20px',
                fontWeight: 'bold',
                color: '#999',
                textAlign: 'center',
                lineHeight: '40px'
              }}>
                -
              </span>
            )}
          </div>
        );
      },
    },
    {
      title: t('templates.visual.colName'),
      dataIndex: 'name',
      key: 'name',
      width: '15%',
      align: 'left' as const,
      render: (text: string, record: TemplateProperty) => (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Input
            value={text}
            onChange={(e) => {
              console.log('属性名称编辑前:', {
                recordName: record.name,
                newValue: e.target.value,
                currentProperties: properties
              });
              
              setIsEditing(true);
              
              const newProperties = properties.map(prop => 
                prop.name === record.name ? { ...prop, name: e.target.value } : prop
              );
              
              console.log('属性名称编辑后:', {
                newProperties: newProperties,
                propertiesLength: newProperties.length
              });
              
              setProperties(newProperties);
              
              // 延迟重置编辑状态
              setTimeout(() => {
                setIsEditing(false);
              }, 100);
              
              // 暂时移除updateTemplateContent调用，避免频繁更新导致卡死
              // updateTemplateContent(newProperties);
            }}
            style={{ border: 'none', padding: 0, background: 'transparent' }}
            placeholder={t('templates.visual.enterFieldName')}
          />
          {record.isRequired && <Tag color="red" style={{ marginLeft: 8 }}>{t('templates.visual.colRequired')}</Tag>}
        </div>
      ),
    },
    {
      title: t('templates.visual.colType'),
      dataIndex: 'type',
      key: 'type',
      align: 'left' as const,
      width: '15%',
      render: (type: string, record: TemplateProperty) => {
        console.log('类型选择器渲染:', {
          recordName: record.name,
          recordType: record.type,
          currentType: type,
          hasChildren: record.children?.length || 0,
          isDisabled: record.type === 'object' && record.children && record.children.length > 0
        });
        
        return (
        <div>
          <Select
            value={type}
            onChange={(value) => {
              console.log('类型选择器onChange触发:', {
                recordName: record.name,
                recordType: record.type,
                newValue: value,
                hasChildren: record.children?.length || 0
              });
              
              // 检查object类型锁定：如果当前是object且有子属性，不允许切换类型
              if (record.type === 'object' && record.children && record.children.length > 0) {
                console.log('object类型锁定：object且有子属性，不允许切换类型');
                message.warning('该对象包含子属性，不允许切换类型。请先删除所有子属性。');
                return; // 不进行类型切换
              }
              
              console.log('准备调用onTopLevelTypeChange:', {
                propertyName: record.name,
                newType: value
              });
              
              onTopLevelTypeChange(record.name, value);
            }}
            disabled={record.type === 'object' && record.children && record.children.length > 0}
            style={{ 
              width: '100%', 
              marginBottom: record.type === 'array' ? 8 : 0,
              opacity: (record.type === 'object' && record.children && record.children.length > 0) ? 0.5 : 1
            }}
          >
            <Select.Option value="string">string</Select.Option>
            <Select.Option value="number">number</Select.Option>
            <Select.Option value="boolean">boolean</Select.Option>
            <Select.Option value="date">date</Select.Option>
            <Select.Option value="array">array</Select.Option>
            <Select.Option value="object">object</Select.Option>
          </Select>
          
          {record.type === 'object' && record.children && record.children.length > 0 && (
            <Text type="warning" style={{ fontSize: '12px' }}>
              {t('templates.visual.typeLockShort')}
              </Text>
          )}
          
          {record.type === 'array' && (
            <div>
              <Text type="secondary" style={{ fontSize: '12px' }}>{t('templates.visual.itemsType')}:</Text>
              <Select
                value={record.itemsType || 'string'}
                onChange={(value) => onArrayItemsTypeChange(record.name, value)}
                disabled={isItemsTypeLocked(record)}
                style={{ width: '100%', marginTop: 4 }}
              >
                <Select.Option value="string">string</Select.Option>
                <Select.Option value="number">number</Select.Option>
                <Select.Option value="boolean">boolean</Select.Option>
                <Select.Option value="date">date</Select.Option>
                <Select.Option value="object">object</Select.Option>
              </Select>
              {isItemsTypeLocked(record) && (
                <Text type="warning" style={{ fontSize: '12px' }}>
                  {t('templates.visual.itemsTypeLockShort')}
              </Text>
              )}
            </div>
          )}
        </div>
        );
      },
    },
    {
      title: t('templates.visual.colDescription'),
      dataIndex: 'description',
      key: 'description',
      width: '40%',
      align: 'left' as const,
      render: (text: string, record: TemplateProperty) => (
        <Input.TextArea
          value={text || ''}
          onChange={(e) => {
            const newProperties = properties.map(prop => 
              prop.name === record.name ? { ...prop, description: e.target.value } : prop
            );
            setProperties(newProperties);
            // 暂时移除updateTemplateContent调用，避免频繁更新导致卡死
            // updateTemplateContent(newProperties);
          }}
          placeholder={t('templates.visual.enterDescription')}
          rows={2}
          style={{ resize: 'vertical' }}
          size="small"
        />
      ),
    },
    {
      title: t('templates.visual.colRequired'),
      key: 'required',
      width: '10%',
      align: 'center' as const,
      render: (_: any, record: TemplateProperty) => (
        <Checkbox
          checked={record.isRequired}
          onChange={(e) => {
            const newProperties = properties.map(prop => 
              prop.name === record.name ? { ...prop, isRequired: e.target.checked } : prop
            );
            setProperties(newProperties);
          }}
        />
      ),
    },
    {
      title: t('templates.visual.colActions'),
      key: 'actions',
      align: 'center' as const,
      width: '10%',
      render: (_: any, record: TemplateProperty, index: number) => (
        <Popconfirm
          title={t('templates.visual.confirmDelete')}
          onConfirm={() => handleDeleteProperty(index)}
          okText={t('common.confirm')}
          cancelText={t('common.cancel')}
        >
          <Button type="text" danger size="small" icon={<DeleteOutlined />} />
        </Popconfirm>
      ),
    },
  ];

  return (
    <div>


      <Card>
        <div style={{ marginBottom: 16 }}>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAddProperty}
          >
            {t('templates.visual.addProperty')}
          </Button>
        </div>

        <Table
          columns={columns}
          dataSource={properties}
          rowKey="uid"
          pagination={false}
          size="small"
          expandable={{
            showExpandColumn: false,
            expandRowByClick: false,
            expandIcon: () => null,
            expandIconColumnIndex: -1
          }}
          components={{
            body: {
              row: (props: any) => {
                const { children, ...restProps } = props;
                const index = properties.findIndex(item => item.uid === restProps['data-row-key']);
                return (
                  <tr
                    {...restProps}
                    onDragOver={(e) => onDragOver(e, index)}
                    onDrop={(e) => onDrop(e, index)}
                    onDragEnter={(e) => onDragEnter(e, index)}
                    onDragLeave={onDragLeave}
                    style={{
                      cursor: 'move',
                      backgroundColor: dragSource === restProps['data-row-key'] ? '#e6f7ff' : (dragOverIndex === index ? '#fffbe6' : 'transparent'),
                      opacity: dragSource === restProps['data-row-key'] ? 0.95 : 1,
                    }}
                  >
                    {children}
                  </tr>
                );
              },
            },
          }}

        />

        {/* 显示展开的子属性 */}
        {properties.map((prop) => {
          console.log('检查子属性显示:', {
            propName: prop.name,
            propType: prop.type,
            propItemsType: prop.itemsType,
            isExpanded: prop.isExpanded,
            hasChildren: prop.children,
            childrenLength: prop.children?.length
          });
          
          if (prop.isExpanded) {
            console.log('展开显示子属性区域:', {
              propName: prop.name,
              propType: prop.type,
              isExpanded: prop.isExpanded,
              hasChildren: prop.children?.length || 0,
              willCallRenderChildren: true
            });
            
            console.log('准备调用renderChildren:', {
              propName: prop.name,
              propType: prop.type,
              children: prop.children,
              childrenLength: prop.children?.length || 0,
              parentPropertyName: prop.name
            });
            
            return (
              <div key={`${prop.name}-children`} style={{ marginTop: 8 }}>
                {renderChildren(prop.children || [], prop.name)}
              </div>
            );
          } else {
            console.log('未展开，不显示子属性区域:', {
              propName: prop.name,
              propType: prop.type,
              isExpanded: prop.isExpanded
            });
            return null;
          }
        })}

      </Card>

      {/* 属性编辑模态框 */}
      <Modal
        title={editingProperty ? t('templates.visual.editProperty') : t('templates.visual.addProperty')}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        width={800}
        style={{ top: 20 }}
        bodyStyle={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}
        footer={[
          <Button key="cancel" onClick={() => setModalVisible(false)}>
            {t('common.cancel')}
          </Button>,
          activeTab === 'manual' ? (
          <Button key="save" type="primary" onClick={handleSaveProperty}>
            {t('common.save')}
            </Button>
          ) : (
            <Button 
              key="addFromSap" 
              type="primary" 
              onClick={createPropertiesFromSapFields}
              disabled={selectedSapFields.length === 0}
            >
              {t('templates.visual.addSelectedFields', { count: selectedSapFields.length })}
            </Button>
          ),
        ]}
      >
        <Tabs 
          activeKey={activeTab} 
          onChange={setActiveTab}
          items={[
            {
              key: 'manual',
              label: (
                <span>
                  <EditOutlined />
                  {t('templates.visual.tabManual')}
                </span>
              ),
              children: (
        <Form form={form} layout="vertical">
          <Form.Item
            name="name"
            label={t('templates.visual.fieldName')}
            rules={[{ required: true, message: t('templates.visual.enterFieldName') }]}
          >
            <Input placeholder={t('templates.visual.enterFieldName')} />
          </Form.Item>

          <Form.Item
            name="type"
            label={t('templates.visual.fieldType')}
            rules={[{ required: true, message: t('templates.visual.chooseFieldType') }]}
          >
            <Select placeholder={t('templates.visual.chooseFieldType')}>
              <Select.Option value="string">string</Select.Option>
              <Select.Option value="number">number</Select.Option>
              <Select.Option value="boolean">boolean</Select.Option>
              <Select.Option value="array">array</Select.Option>
              <Select.Option value="object">object</Select.Option>
              <Select.Option value="date">date</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="description"
            label={t('templates.visual.description')}
          >
            <Input.TextArea rows={3} placeholder={t('templates.visual.enterDescription')} />
          </Form.Item>

          <Form.Item
            name="isRequired"
            label={t('templates.visual.isRequired')}
            valuePropName="checked"
          >
            <input type="checkbox" />
          </Form.Item>

          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) => 
              prevValues.type !== currentValues.type
            }
          >
            {({ getFieldValue }) => {
              const type = getFieldValue('type');
              if (type === 'array') {
                return (
                  <>
                    <Form.Item
                      name="itemsType"
                      label={t('templates.visual.itemsType')}
                      rules={[{ required: true, message: t('templates.visual.chooseItemsType') }]}
                    >
                      <Select placeholder={t('templates.visual.chooseItemsType')}>
                        <Select.Option value="string">string</Select.Option>
                        <Select.Option value="number">number</Select.Option>
                        <Select.Option value="boolean">boolean</Select.Option>
                        <Select.Option value="object">object</Select.Option>
                        <Select.Option value="date">date</Select.Option>
                      </Select>
                    </Form.Item>

                    <Form.Item
                      name="itemsDescription"
                      label={t('templates.visual.itemsDescription')}
                    >
                      <Input.TextArea rows={2} placeholder={t('templates.visual.enterItemsDescription')} />
                    </Form.Item>
                  </>
                );
              }
              return null;
            }}
          </Form.Item>
        </Form>
              ),
            },
            {
              key: 'sap',
              label: (
                <span>
                  <DatabaseOutlined />
                  {t('templates.visual.tabSap')}
                </span>
              ),
              children: (
                <div>
                  <Alert
                    message={t('templates.visual.sapSelectTitle')}
                    description={t('templates.visual.sapSelectDesc')}
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                  />
                  <SapFieldSelector
                    onFieldsSelected={handleSapFieldsSelected}
                    selectedFields={selectedSapFields}
                    multiple={true}
                    showServerSelector={false}
                    showAdvancedConfig={false}
                  />
                </div>
              ),
            },
          ]}
        />
      </Modal>

      {/* 子属性选择模态框 */}
      <Modal
        title={t('templates.visual.addChildFor', { name: parentPropertyName })}
        open={childModalVisible}
        onCancel={() => setChildModalVisible(false)}
        width={800}
        style={{ top: 20 }}
        bodyStyle={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}
        footer={[
          <Button key="cancel" onClick={() => setChildModalVisible(false)}>
            {t('common.cancel')}
          </Button>,
          childActiveTab === 'manual' ? (
            <Button key="addManual" type="primary" onClick={() => addManualNestedProperty(parentPropertyName)}>
              {t('templates.visual.addChild')}
            </Button>
          ) : (
            <Button 
              key="addFromSap" 
              type="primary" 
              onClick={createChildPropertiesFromSapFields}
              disabled={childSelectedSapFields.length === 0}
            >
              {t('templates.visual.addSelectedFields', { count: childSelectedSapFields.length })}
            </Button>
          ),
        ]}
      >
        <Tabs 
          activeKey={childActiveTab} 
          onChange={setChildActiveTab}
          items={[
            {
              key: 'manual',
              label: (
                <span>
                  <EditOutlined />
                  {t('templates.visual.tabManual')}
                </span>
              ),
              children: (
                <div style={{ textAlign: 'center', padding: '40px 0' }}>
                  <p>{t('templates.visual.manualAddDesc1')}</p>
                  <p style={{ color: '#666', fontSize: '14px' }}>
                    {t('templates.visual.manualAddDesc2')}
                  </p>
                </div>
              ),
            },
            {
              key: 'sap',
              label: (
                <span>
                  <DatabaseOutlined />
                  {t('templates.visual.tabSap')}
                </span>
              ),
              children: (
                <div>
                  <Alert
                    message={t('templates.visual.sapChildTitle')}
                    description={t('templates.visual.sapChildDesc', { name: parentPropertyName })}
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                  />
                  <SapFieldSelector
                    onFieldsSelected={handleChildSapFieldsSelected}
                    selectedFields={childSelectedSapFields}
                    multiple={true}
                    showServerSelector={false}
                    showAdvancedConfig={false}
                  />
                </div>
              ),
            },
          ]}
        />
      </Modal>
    </div>
  );
};

export default TemplateVisualEditor;
